import React from 'react';
import { styled } from '@compiled/react';
import { graphql, useFragment } from 'react-relay';
import type { UIAnalyticsEvent } from '@atlaskit/analytics-next';
import Button from '@atlaskit/button';
import BranchIcon from '@atlaskit/icon/glyph/bitbucket/branches';
import CommitIcon from '@atlaskit/icon/glyph/bitbucket/commits';
import PullRequestIcon from '@atlaskit/icon/glyph/bitbucket/pullrequests';
import { colors } from '@atlaskit/theme';
import { token } from '@atlaskit/tokens';
import Tooltip from '@atlaskit/tooltip';
import { gridSize } from '@atlassian/jira-common-styles/src/main.tsx';
import { VERSION_DETAILS } from '@atlassian/jira-development-board-common/src/common/constants.tsx';
import { DevInfoTypes } from '@atlassian/jira-development-board-common/src/types.tsx';
import DevInfoIcon from '@atlassian/jira-development-board-dev-info-icon/src/main.tsx';
import type { LegacyData } from '@atlassian/jira-development-details-loader/src/common/types/index.tsx';
import {
	COMMIT_PANEL_DATA_TYPE,
	BRANCH_PANEL_DATA_TYPE,
	PULLREQUEST_PANEL_DATA_TYPE,
	REVIEW_PANEL_DATA_TYPE,
} from '@atlassian/jira-development-details/src/common/model/constants.tsx';
import type { PanelDataTypes } from '@atlassian/jira-development-details/src/main/model/types.tsx';
import { useIntl } from '@atlassian/jira-intl';
import { fireUIAnalytics } from '@atlassian/jira-product-analytics-bridge';
import type { development_softwareReleasesVersionDetailIssueList$key } from '@atlassian/jira-relay/src/__generated__/development_softwareReleasesVersionDetailIssueList.graphql';
import { ISSUE_CARD_CLICK_IGNORE_TARGET } from '@atlassian/jira-software-releases-version-detail-issue-card-click-prevent-controller/src/common/constants.tsx';
import { DEV_DETAILS_DIALOG_SCOPE_ID } from '../../../../common/constants.tsx';
import messages from './messages.tsx';
import { CustomPullRequestIcon } from './pullrequest-icon/index.tsx';
import {
	mapPullRequestStateToIconColour,
	mapReviewStateToIconColour,
	toDevInfoType,
	toReviewState,
} from './utils.tsx';

type Props = {
	jiraIssue: development_softwareReleasesVersionDetailIssueList$key;
	onClick: (dataType: PanelDataTypes, reviewData?: LegacyData) => void;
};

export const Development = ({ jiraIssue, onClick }: Props) => {
	const { formatMessage } = useIntl();

	const { issueId, devSummaryCache } = useFragment(
		graphql`
			fragment development_softwareReleasesVersionDetailIssueList on JiraIssue {
				issueId
				devSummaryCache @required(action: THROW) {
					devSummary @required(action: THROW) {
						branch @required(action: THROW) {
							overall @required(action: THROW) {
								count @required(action: THROW)
							}
						}
						commit @required(action: THROW) {
							overall @required(action: THROW) {
								count @required(action: THROW)
							}
						}
						pullrequest @required(action: THROW) {
							overall @required(action: THROW) {
								count @required(action: THROW)
								state
							}
						}
						review @required(action: THROW) {
							overall @required(action: THROW) {
								count @required(action: THROW)
								state
								lastUpdated
								stateCount @required(action: THROW)
							}
							summaryByProvider @required(action: THROW) {
								providerId
								name
								count
							}
						}
					}
				}
			}
		`,
		jiraIssue,
	);

	const { branch, commit, pullrequest, review } = devSummaryCache.devSummary;
	const pullRequestCount = pullrequest.overall.count;
	const pullRequestState = pullrequest.overall.state;

	if (pullRequestCount > 0 && pullRequestState) {
		return (
			<DevelopmentContainer
				data-version-details-issue-click-ignore-target={ISSUE_CARD_CLICK_IGNORE_TARGET}
			>
				<Button
					appearance="subtle-link"
					onClick={(_, analyticsEvent: UIAnalyticsEvent) => {
						onClick(PULLREQUEST_PANEL_DATA_TYPE);
						fireUIAnalytics(analyticsEvent, 'versionsPullRequestLozenge');
					}}
				>
					<VerticalCenter>
						<DevInfoIcon
							devInfoType={DevInfoTypes.PULLREQUEST}
							issueId={Number(issueId)}
							customTrigger={
								<CustomPullRequestIcon
									pullRequestState={toDevInfoType(pullrequest.overall.state)}
									label={formatMessage(messages.pullRequestIconLabel)}
									primaryColor={mapPullRequestStateToIconColour(pullrequest.overall.state)}
								/>
							}
							isStopPropagationOnClick={false}
							scopeId={DEV_DETAILS_DIALOG_SCOPE_ID}
							location={VERSION_DETAILS}
						/>
					</VerticalCenter>
				</Button>
			</DevelopmentContainer>
		);
	}

	const reviewCount = review.overall.count;
	const reviewState = review.overall.state;

	if (reviewCount > 0 && reviewState) {
		return (
			<DevelopmentContainer
				data-version-details-issue-click-ignore-target={ISSUE_CARD_CLICK_IGNORE_TARGET}
			>
				<Tooltip
					content={formatMessage(messages.reviewCount, {
						reviewCount,
					})}
					position="bottom-start"
				>
					<Button
						appearance="subtle-link"
						onClick={(_, analyticsEvent: UIAnalyticsEvent) => {
							// eslint-disable-next-line @typescript-eslint/no-explicit-any
							const reviewDataByProvider: Record<string, any> = {};
							review.summaryByProvider.forEach(({ providerId, name, count }) => {
								if (providerId != null) {
									reviewDataByProvider[providerId] = { name, count };
								}
							});

							onClick(REVIEW_PANEL_DATA_TYPE, {
								overall: {
									count: review.overall.count,
									lastUpdated: review.overall.lastUpdated ?? '',
									state: toReviewState(reviewState),
									stateCount: review.overall.stateCount,
								},
								byInstanceType: reviewDataByProvider,
							});
							fireUIAnalytics(analyticsEvent, 'versionsReviewLozenge');
						}}
					>
						<VerticalCenter>
							{/* eslint-disable-next-line @atlaskit/design-system/no-legacy-icons */}
							<PullRequestIcon
								label={formatMessage(messages.reviewIconLabel)}
								primaryColor={mapReviewStateToIconColour(reviewState)}
							/>
						</VerticalCenter>
					</Button>
				</Tooltip>
			</DevelopmentContainer>
		);
	}

	const branchCount = branch.overall.count;

	if (branchCount > 0) {
		return (
			<DevelopmentContainer
				data-version-details-issue-click-ignore-target={ISSUE_CARD_CLICK_IGNORE_TARGET}
			>
				<Button
					appearance="subtle-link"
					onClick={(_, analyticsEvent: UIAnalyticsEvent) => {
						onClick(BRANCH_PANEL_DATA_TYPE);
						fireUIAnalytics(analyticsEvent, 'versionsBranchLozenge');
					}}
				>
					<VerticalCenter>
						<DevInfoIcon
							devInfoType={DevInfoTypes.BRANCH}
							issueId={Number(issueId)}
							customTrigger={
								<BranchIcon
									label={formatMessage(messages.branchIconLabel)}
									primaryColor={token('color.icon', colors.N500)}
								/>
							}
							scopeId={DEV_DETAILS_DIALOG_SCOPE_ID}
							location={VERSION_DETAILS}
						/>
					</VerticalCenter>
				</Button>
			</DevelopmentContainer>
		);
	}

	const commitCount = commit.overall.count;

	if (commitCount > 0) {
		return (
			<DevelopmentContainer
				data-version-details-issue-click-ignore-target={ISSUE_CARD_CLICK_IGNORE_TARGET}
			>
				<VerticalCenter>
					<DevInfoIcon
						devInfoType={DevInfoTypes.COMMIT}
						issueId={Number(issueId)}
						customTrigger={
							<Button
								appearance="subtle-link"
								onClick={(_, analyticsEvent: UIAnalyticsEvent) => {
									onClick(COMMIT_PANEL_DATA_TYPE);
									fireUIAnalytics(analyticsEvent, 'versionsCommitLozenge');
								}}
							>
								<CommitIcon
									label={formatMessage(messages.commitIconLabel)}
									primaryColor={token('color.icon', colors.N500)}
								/>
							</Button>
						}
						scopeId={DEV_DETAILS_DIALOG_SCOPE_ID}
						location={VERSION_DETAILS}
					/>
				</VerticalCenter>
			</DevelopmentContainer>
		);
	}

	return <DevelopmentContainer />;
};

// eslint-disable-next-line @atlaskit/ui-styling-standard/no-styled, @atlaskit/ui-styling-standard/no-exported-styles -- Ignored via go/DSP-18766
export const DevelopmentContainer = styled.div({
	// eslint-disable-next-line @atlaskit/ui-styling-standard/no-imported-style-values, @atlaskit/ui-styling-standard/no-unsafe-values -- Ignored via go/DSP-18766
	width: `${gridSize * 6}px`,
	// eslint-disable-next-line @atlaskit/ui-styling-standard/no-imported-style-values, @atlaskit/ui-styling-standard/no-unsafe-values -- Ignored via go/DSP-18766
	minWidth: `${gridSize * 6}px`,
});

// eslint-disable-next-line @atlaskit/ui-styling-standard/no-styled -- To migrate as part of go/ui-styling-standard
const VerticalCenter = styled.div({
	display: 'flex',
	alignItems: 'center',
});
